import { useContext, useEffect, useState } from "react";
import ExerciseLibraryModal from "./ExerciseLibraryModal";
import { Plus, Copy, Trash2, X, Video, GripVertical, ChevronDown } from 'lucide-react';
import { ProgramContext } from "../../context/ProgramsContext";
import toast from "react-hot-toast";

const CreatePtSessionModal = ({ onClose, onSave, onOpenViewer }) => {
    const {
        generateUniqueId,
        exerciseTemplates
    } = useContext(ProgramContext)
    const [sessionName, setSessionName] = useState('');
    const [sessionDescription, setSessionDescription] = useState('');
    const [exercises, setExercises] = useState([]);
    const [isLibraryOpen, setLibraryOpen] = useState(false);
    const [draggable, setDraggable] = useState(false)
    const [setsInputValues, setSetsInputValues] = useState({});

    const handleAddExercises = (selectedExercises) => {
        const newExercises = selectedExercises.map(ex => ({ ...ex, reps: 8, hold: ex.hasHold ? 5 : 0, set_limb_instruction: null, instanceId: generateUniqueId() }));
        setExercises(prev => [...prev, ...newExercises]);
    };

    const handleConfigChange = (instanceId, field, value) => {
        setExercises((prev) => {
            // Find the set_id of the instance being changed
            const target = prev.find(ex => ex.instanceId === instanceId);
            if (!target) return prev;

            const targetSetId = target.set_id;

            return prev.map((ex) => {
                if (ex.set_id !== targetSetId) return ex;

                // Special case: update nested field in exercise_data
                if (field === 'hold_in_seconds') {
                    return {
                        ...ex,
                        hold_in_seconds: value,
                        hold: value,
                        exercise_data: {
                            ...ex.exercise_data,
                            hold_in_seconds: value,
                            hold: value
                        },
                    };
                }

                if (field === 'reps') {
                    return {
                        ...ex,
                        reps: value,
                        exercise_data: {
                            ...ex.exercise_data,
                            reps: value,
                        },
                    };
                }

                // Default: top-level field
                return {
                    ...ex,
                    [field]: value,
                };
            });
        });
    };

    const handleDuplicate = (instanceId) => setExercises(p => { const index = p.findIndex(ex => ex.instanceId === instanceId); if (index === -1) return p; const newP = [...p]; newP.splice(index + 1, 0, { ...p[index], instanceId: generateUniqueId(), set_id: generateUniqueId() }); return newP; });
    const handleDelete = (instanceId) => setExercises(p => p.filter(ex => ex.instanceId !== instanceId));
    // const handleDragStart = (e, index) => e.dataTransfer.setData('exercise-index', index);
    // const handleDrop = (e, dropIndex) => { const dragIndex = parseInt(e.dataTransfer.getData('exercise-index'), 10); setExercises(p => { const newP = [...p]; const [dragged] = newP.splice(dragIndex, 1); newP.splice(dropIndex, 0, dragged); return newP; }); };
    const handleDragStart = (e, uiIndex) => {
        e.dataTransfer.setData('dragged-ui-index', uiIndex);
    };
    const handleDrop = (e, uiDropIndex) => {
        const uiDragIndex = parseInt(e.dataTransfer.getData('dragged-ui-index'), 10);

        // Step 1: Create UI list of unique set_id items (shown on UI)
        const uiList = exercises.filter(
            (ex, index, self) => index === self.findIndex(e => e.set_id === ex.set_id)
        );

        const dragSetId = uiList[uiDragIndex]?.set_id;
        const dropSetId = uiList[uiDropIndex]?.set_id;

        if (!dragSetId || !dropSetId) return;

        setExercises(prev => {
            // Extract all groups
            const draggedGroup = prev.filter(item => item.set_id === dragSetId);
            const remaining = prev.filter(item => item.set_id !== dragSetId);

            // Determine where in the full list to insert: find index of dropSetId in remaining
            const targetIndexInRemaining = remaining.findIndex(item => item.set_id === dropSetId);

            const newList = [
                ...remaining.slice(0, targetIndexInRemaining),
                ...draggedGroup,
                ...remaining.slice(targetIndexInRemaining),
            ];

            return newList;
        });
    };

    const handleSave = () => {
        if (exercises?.length > 0) {
            const newSession = {
                id: generateUniqueId(),
                type: 'PT_SESSION',
                title: sessionName || 'Custom PT Session',
                description: sessionDescription,
                estTime: exercises.length * 5,
                exerciseCount: exercises.length,
                exercises: exercises,
            };
            onSave(newSession);
        } else {
            toast.error("Please add exercises")
        }
    };

    useEffect(() => {
        exercises.forEach((ex) => {
            if (ex.limb === 'Y' && ex?.limb_instructions?.length > 0 && !ex?.set_limb_instruction) {
                handleConfigChange(ex.instanceId, 'set_limb_instruction', ex?.limb_instructions[0]);
            }
            // if (!ex?.exercise_data?.hold_in_seconds) {
            //     handleConfigChange(ex.instanceId, 'hold_in_seconds', 0)
            // }
            if (ex?.reps >= 0) {
                handleConfigChange(ex.instanceId, 'reps', ex.reps)
            }
            if (ex?.exercise_data?.hold_in_seconds >= 0) {
                handleConfigChange(ex.instanceId, 'hold_in_seconds', ex?.exercise_data?.hold_in_seconds)
            }
        });
    }, [exercises?.length]);

    return (<>
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-5xl p-6 transform transition-all flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center mb-4 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Create New PT Session</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition">
                        <X size={24} />
                    </button>
                </div>

                {/* Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                        type="text"
                        placeholder="PT Session Name"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#166273]/30"
                    />
                    <input
                        type="text"
                        placeholder="PT Session Description"
                        value={sessionDescription}
                        onChange={(e) => setSessionDescription(e.target.value)}
                        className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#166273]/30"
                    />
                </div>

                {/* Exercises List */}
                <div className="flex-grow overflow-y-auto pr-2">
                    {/* {console.log("exercises", exercises)} */}
                    {exercises.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">No exercises added yet.</div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            {exercises
                                ?.filter(
                                    (ex, index, self) =>
                                        index === self.findIndex((e) => e.set_id === ex.set_id)
                                )
                                ?.map((ex, index) => {
                                    const sameIdCount = exercises.filter(e => e.set_id === ex.set_id).length;
                                    const limbOptions = ex?.limb_instructions || [];
                                    const inputValue = setsInputValues[ex.instanceId] ?? sameIdCount;
                                    const exerciseTemplateExercise = exerciseTemplates?.find((e) => e?.name === ex?.name)

                                    return (
                                        <div
                                            key={ex.instanceId}
                                            draggable={draggable}
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragOver={(e) => e.preventDefault()}
                                            className="flex items-center gap-4 p-3 border rounded-lg mb-2 bg-gray-50"
                                        >
                                            <GripVertical
                                                onMouseOver={() => setDraggable(true)}
                                                onMouseOut={() => setDraggable(false)}
                                                className="cursor-grab text-gray-400" />

                                            <div className="flex-1 font-semibold text-gray-800 min-w-[150px]">{ex.name}</div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 items-start w-full">

                                                {/* Max ROM */}
                                                <div className="min-w-0 flex flex-col text-sm">
                                                    <span className="whitespace-nowrap mb-1">Max ROM:</span>
                                                    <div className="relative">
                                                        <select
                                                            value={ex?.exercise_data?.have_rom !== 1 ? "" : ex?.target_rom_max}
                                                            onChange={(e) => handleConfigChange(ex.instanceId, 'target_rom_max', e.target.value)}
                                                            disabled={ex?.exercise_data?.have_rom !== 1}
                                                            className="w-full min-w-[80px] border rounded-md px-3 py-2 appearance-none focus:outline-none focus:ring-2 focus:ring-[#166273]/30 border-gray-300 bg-white disabled:opacity-50"
                                                        >
                                                            {
                                                                ex?.exercise_data?.have_rom !== 1 &&
                                                                <option value="" disabled>N/A</option>
                                                            }
                                                            {["40%", "50%", "60%", "70%", "80%", "90%", "100%"].map((type) => (
                                                                <option key={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
                                                    </div>
                                                </div>

                                                {/* Sets */}
                                                <div className="min-w-0 flex flex-col text-sm">
                                                    <span className="whitespace-nowrap mb-1">Sets:</span>
                                                    <div className="flex items-stretch border text-center rounded w-full overflow-hidden">
                                                        <button
                                                            type="button"
                                                            className="text-white bg-[#166273] disabled:opacity-50 px-2"
                                                            onClick={() => {
                                                                const lastIndex = [...exercises].reverse().findIndex(e => e.set_id === ex.set_id);
                                                                if (lastIndex !== -1) {
                                                                    const removeIndex = exercises.length - 1 - lastIndex;
                                                                    setExercises(prev => prev.filter((_, idx) => idx !== removeIndex));
                                                                }
                                                            }}
                                                            disabled={sameIdCount <= 1}
                                                        >-</button>

                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={10}
                                                            value={inputValue}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value > 10) {
                                                                    toast.dismiss()
                                                                    toast.error("Sets cannot be greater than 10")
                                                                    return;
                                                                };
                                                                setSetsInputValues(prev => ({ ...prev, [ex.instanceId]: value }));
                                                            }}
                                                            onBlur={(e) => {
                                                                let newCount = parseInt(e.target.value);
                                                                if (isNaN(newCount) || newCount < 1) newCount = 1;
                                                                if (newCount > 10) newCount = 10;

                                                                const diff = newCount - sameIdCount;

                                                                if (diff > 0) {
                                                                    const duplicates = Array.from({ length: diff }, () => ({
                                                                        ...ex,
                                                                        instanceId: generateUniqueId()
                                                                    }));
                                                                    // setExercises(prev => [...prev, ...duplicates]);
                                                                    setExercises(prev => {
                                                                        const newExercises = [...prev];
                                                                        const lastIndex = [...prev]
                                                                            .map((item, i) => ({ ...item, i }))
                                                                            .filter(item => item.set_id === ex.set_id)
                                                                            .map(item => item.i)
                                                                            .pop();

                                                                        const insertAt = lastIndex !== undefined ? lastIndex + 1 : newExercises.length;
                                                                        newExercises.splice(insertAt, 0, ...duplicates);
                                                                        return newExercises;
                                                                    });
                                                                } else if (diff < 0) {
                                                                    // Remove from the end
                                                                    const removeCount = Math.abs(diff);
                                                                    setExercises(prev => {
                                                                        let count = 0;
                                                                        const reversed = [...prev].reverse();
                                                                        const filteredReversed = reversed.filter(item => {
                                                                            if (item.set_id === ex.set_id && count < removeCount) {
                                                                                count++;
                                                                                return false;
                                                                            }
                                                                            return true;
                                                                        });
                                                                        return filteredReversed.reverse();
                                                                    });
                                                                }

                                                                // Clear temp input state
                                                                setSetsInputValues(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated[ex.instanceId];
                                                                    return updated;
                                                                });
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.target.blur(); // triggers blur logic
                                                                }
                                                            }}
                                                            className="w-full py-2 text-center bg-transparent outline-none disabled:opacity-50 appearance-none 
                                                        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="text-white bg-[#166273] disabled:opacity-30 px-2"
                                                            onClick={() => {
                                                                const duplicate = { ...ex, instanceId: generateUniqueId() };
                                                                // setExercises(prev => [...prev, duplicate]);
                                                                setExercises(prev => {
                                                                    const newExercises = [...prev];
                                                                    const lastIndex = prev
                                                                        .map((item, i) => ({ ...item, i }))
                                                                        .filter(item => item.set_id === ex.set_id)
                                                                        .map(item => item.i)
                                                                        .pop();

                                                                    const insertAt = lastIndex !== undefined ? lastIndex + 1 : newExercises.length;
                                                                    newExercises.splice(insertAt, 0, duplicate);
                                                                    return newExercises;
                                                                });
                                                            }}
                                                            disabled={sameIdCount >= 10}
                                                        >+</button>
                                                    </div>
                                                </div>

                                                {/* Reps */}
                                                <div className="min-w-0 flex flex-col text-sm">
                                                    <span className="mb-1">Reps:</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max={1000}
                                                        value={ex.reps || ''}
                                                        onChange={(e) => handleConfigChange(ex.instanceId, 'reps', parseInt(e.target.value))}
                                                        onBlur={(e) => {
                                                            if (e.target.value > 1000) {
                                                                toast.dismiss()
                                                                toast.error("Reps cannot be greater than 1000")
                                                                handleConfigChange(ex.instanceId, 'reps', parseInt(1000))
                                                                return;
                                                            }
                                                            if (e.target.value < 1 || !e.target.value) {
                                                                handleConfigChange(ex.instanceId, 'reps', parseInt(1))
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.target.blur();
                                                            }
                                                        }}
                                                        className="w-full border rounded px-2 py-2 text-sm"
                                                    />
                                                </div>

                                                {/* Hold */}
                                                <div className="min-w-0 flex flex-col text-sm">
                                                    <span className="whitespace-nowrap mb-1">Hold (s):</span>
                                                    <input
                                                        type={"number"}
                                                        min="0"
                                                        max={3600}
                                                        value={ex?.exercise_data?.hold_in_seconds}
                                                        disabled={!exerciseTemplateExercise?.exercise_data?.hold_in_seconds}
                                                        onChange={(e) => {
                                                            if (e.target.value >= 0) {
                                                                handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(e.target.value))
                                                            } else {
                                                                handleConfigChange(ex.instanceId, 'hold_in_seconds', 0)
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            if (e.target.value > 3600) {
                                                                toast.dismiss()
                                                                toast.error("Hold cannot be greater than 3600s")
                                                                handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(3600))
                                                                return;
                                                            }
                                                            if (e.target.value < 0 || !e.target.value) {
                                                                handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(0))
                                                            } else if (e.target.value > 0 && e.target.value < 4) {
                                                                handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(4))
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.target.blur();
                                                            }
                                                        }}
                                                        className="w-full border rounded px-2 py-2 text-sm disabled:opacity-50"
                                                    />
                                                </div>

                                                {/* Limb Selector */}
                                                <div className="min-w-0 flex flex-col text-sm relative">
                                                    <span className="mb-1">Limb:</span>
                                                    <select
                                                        value={ex.set_limb_instruction?.name || ''}
                                                        onChange={(e) => {
                                                            const selected = limbOptions.find(obj => obj.name === e.target.value);
                                                            handleConfigChange(ex.instanceId, 'set_limb_instruction', selected);
                                                        }}
                                                        disabled={ex.limb !== 'Y'}
                                                        className="w-full border rounded-md px-3 py-2 pr-10 appearance-none focus:outline-none focus:ring-2 focus:ring-[#166273]/30 border-gray-300 disabled:opacity-50"
                                                    >
                                                        {
                                                            ex.limb !== 'Y' &&
                                                            <option value="">N/A</option>
                                                        }
                                                        {limbOptions.map((instruction, idx) => (
                                                            <option key={idx} value={instruction.name}>{instruction.name}</option>
                                                        ))}
                                                    </select>
                                                    <ChevronDown className="absolute right-3 top-[40px] transform -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
                                                </div>
                                            </div>




                                            {/* Actions */}
                                            <div className="flex items-center gap-3 justify-end min-w-[150px]">
                                                <button
                                                    onClick={() => onOpenViewer({ ...ex, title: ex.name, content: ex.videoUrl, category: 'Video' })}
                                                    className="text-[#166273] hover:text-[#166273]/90 transition"
                                                >
                                                    <Video size={20} />
                                                </button>
                                                <button
                                                    onClick={() => handleDuplicate(ex.instanceId)}
                                                    className="text-gray-500 hover:text-gray-700 transition disabled:opacity-[0.4] disabled:cursor-not-allowed"
                                                >
                                                    <Copy size={20} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(ex.instanceId)}
                                                    className="text-red-500 hover:text-red-700 transition"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                    )}
                </div>

                {/* Footer Buttons */}
                <div className="mt-6 pt-4 border-t flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
                    <button
                        onClick={() => setLibraryOpen(true)}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#166273]/10 text-[#166273] font-semibold rounded-lg hover:bg-[#166273]/20 transition"
                    >
                        <Plus size={18} />
                        Add Exercises
                    </button>
                    <div className="flex gap-2 justify-end">
                        {/* <button className="px-4 py-2 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition">
                            Save as Template
                        </button> */}
                        <button
                            disabled={!sessionName || exercises?.length <= 0}
                            onClick={handleSave}
                            className="px-6 py-3 bg-[#166273] text-white font-semibold rounded-lg shadow-md hover:bg-[#166273]/90 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            Add Session to Plan
                        </button>
                    </div>
                </div>
            </div>
        </div>
        {console.log("exercises", exercises)}
        {isLibraryOpen && <ExerciseLibraryModal onAdd={handleAddExercises} onClose={() => setLibraryOpen(false)} selectedExercisesList={exercises} />}
    </>);
};

export default CreatePtSessionModal;